import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Modal, { Field, useModalClose } from './Modal';
import { Chip } from './StatusChip';
import ErrorState from './ErrorState';
import EmptyState from './EmptyState';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { formatDate } from '../utils/format';
import { checkEmail, checkWebsite } from '../utils/clientSectionSpecs';
import {
  REPORT_TYPES, REPORT_TYPE_INDEX, BRANDING_OPTIONS, DELIVERY_OPTIONS,
  PHOTO_OPTIONS, STRUCTURED_OPTIONS, OUTPUT_FORMATS, SUB_FREQUENCIES,
} from '../utils/reportTypes';

/**
 * <ClientReporting /> — how reports for one client are produced and delivered.
 *
 * Report types are listed grouped, each showing whether it's configured for this
 * client or falling back to the CloudSafe default. Configuring one opens a
 * dialog rather than expanding inline, because there are a dozen settings per
 * type and an accordion of those is unreadable.
 */

const card = { background: 'var(--cw-surface)', border: '1px solid var(--cw-border)', borderRadius: 10, padding: 18 };
const h3 = { fontWeight: 700, color: 'var(--cw-text)', margin: 0, fontSize: 'var(--cs-text-md)' };
const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 12 };

/** "a@b.com, c@d.co.uk" → first bad address message, or null. */
const checkEmailList = (v) => {
  const list = String(v || '').split(/[,;]/).map((x) => x.trim()).filter(Boolean);
  const bad = list.find((x) => checkEmail(x));
  return bad ? `“${bad}” isn’t a valid email address` : null;
};
const CancelButton = ({ disabled }) => {
  const requestClose = useModalClose();
  return <button type="button" className="cw-btn cw-btn-secondary" onClick={requestClose} disabled={disabled}>Cancel</button>;
};
const SUB_INIT = { name: '', frequency: 'monthly', recipients: '', contents: '', output_format: 'pdf', report_template_id: '' };

const labelOf = (opts, v) => (opts.find(([k]) => k === v) || [null, v])[1];

const DEFAULT_CFG = {
  branding: 'contractor', delivery: 'email', photo_handling: 'appendix',
  include_appendices: 1, include_previous_findings: 0, include_asset_register: 0,
  separate_photo_file: 0, structured_export: 'none', output_format: 'pdf',
  doc_template_id: '', recipients: '', cc_recipients: '', subject_template: '',
  cover_reference: '', cover_notes: '', structured_endpoint: '', notes: '',
};

const ClientReporting = ({ clientId, clientName }) => {
  const [configs, setConfigs] = useState([]);
  const [docTemplates, setDocTemplates] = useState([]);
  const [dataTemplates, setDataTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const { confirm } = useConfirm();

  const [editing, setEditing] = useState(null);   // report type key
  const [form, setForm] = useState(DEFAULT_CFG);
  const [formInitial, setFormInitial] = useState(DEFAULT_CFG);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState(null);

  const [subOpen, setSubOpen] = useState(false);
  const [subForm, setSubForm] = useState(SUB_INIT);
  const [subErrors, setSubErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        apiFetch(`/api/contractor/clients/${clientId}/reporting`),
        apiFetch(`/api/contractor/clients/${clientId}/report-subscriptions`),
      ]);
      setConfigs(Array.isArray(d?.configs) ? d.configs : []);
      setDocTemplates(Array.isArray(d?.docTemplates) ? d.docTemplates : []);
      setDataTemplates(Array.isArray(d?.dataTemplates) ? d.dataTemplates : []);
      setContacts(Array.isArray(d?.contacts) ? d.contacts : []);
      setSubs(Array.isArray(s?.subscriptions) ? s.subscriptions : []);
      setLoadError(null);
    } catch (e) { setLoadError(e); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const byType = useMemo(() => {
    const m = {};
    configs.forEach((c) => { m[c.report_type] = c; });
    return m;
  }, [configs]);

  const configuredCount = configs.length;

  const openEdit = (key) => {
    const existing = byType[key];
    const f = existing ? { ...DEFAULT_CFG, ...Object.fromEntries(Object.entries(existing).filter(([, v]) => v !== null)) } : DEFAULT_CFG;
    setForm(f); setFormInitial(f); setErrors({}); setModalError(null);
    setEditing(key);
  };

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((x) => ({ ...x, [k]: null })); };

  const save = async () => {
    const errs = {};
    const r = checkEmailList(form.recipients); if (r) errs.recipients = r;
    const c = checkEmailList(form.cc_recipients); if (c) errs.cc_recipients = c;
    if (form.structured_export === 'api') {
      if (!String(form.structured_endpoint || '').trim()) errs.structured_endpoint = 'Enter the endpoint address';
      else if (!/^https:\/\//i.test(String(form.structured_endpoint).trim()) || checkWebsite(form.structured_endpoint)) errs.structured_endpoint = 'Enter an https:// address';
    }
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true); setModalError(null);
    try {
      await apiFetch(`/api/contractor/clients/${clientId}/reporting/${editing}`, {
        method: 'PUT', body: JSON.stringify(form),
      });
      toast.success(`${REPORT_TYPE_INDEX[editing]?.label || 'Report'} settings saved`);
      setEditing(null);
      await load();
    } catch (e) { setModalError(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const reset = async (key) => {
    const label = REPORT_TYPE_INDEX[key]?.label || 'this report type';
    const ok = await confirm({ title: `Reset ${label} to the CloudSafe default?`, body: 'This client’s template, branding and delivery settings for it are removed.', confirmLabel: 'Reset to default', tone: 'danger' });
    if (!ok) return;
    try {
      await apiFetch(`/api/contractor/clients/${clientId}/reporting/${key}`, { method: 'DELETE' });
      toast.success(`${label} reset to the default`);
      await load();
    } catch (e) { toast.error(`Couldn't reset it: ${apiErrorMessage(e)}`); }
  };

  const setSub = (k, v) => { setSubForm((f) => ({ ...f, [k]: v })); if (subErrors[k]) setSubErrors((x) => ({ ...x, [k]: null })); };
  const addSub = async () => {
    const errs = {};
    if (!subForm.name.trim()) errs.name = 'Give the report a name';
    const r = checkEmailList(subForm.recipients); if (r) errs.recipients = r;
    setSubErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true); setModalError(null);
    try {
      await apiFetch(`/api/contractor/clients/${clientId}/report-subscriptions`, {
        method: 'POST', body: JSON.stringify({ ...subForm, name: subForm.name.trim() }),
      });
      toast.success(`${subForm.name.trim()} added`);
      setSubOpen(false);
      setSubForm(SUB_INIT);
      await load();
    } catch (e) { setModalError(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const toggleSub = async (s) => {
    const on = !!Number(s.enabled);
    try {
      await apiFetch(`/api/contractor/clients/${clientId}/report-subscriptions/${s.id}`, {
        method: 'PUT', body: JSON.stringify({ enabled: on ? 0 : 1 }),
      });
      toast.success(on ? `${s.name} paused` : `${s.name} resumed`);
      await load();
    } catch (e) { toast.error(`Couldn't ${on ? 'pause' : 'resume'} it: ${apiErrorMessage(e)}`); }
  };

  const removeSub = async (s) => {
    const ok = await confirm({ title: `Remove the recurring report “${s.name}”?`, body: 'This client stops receiving it.', confirmLabel: 'Remove report', tone: 'danger' });
    if (!ok) return;
    try {
      await apiFetch(`/api/contractor/clients/${clientId}/report-subscriptions/${s.id}`, { method: 'DELETE' });
      toast.success(`${s.name} removed`);
      await load();
    } catch (e) { toast.error(`Couldn't remove it: ${apiErrorMessage(e)}`); }
  };
  const FormError = ({ message }) => (message ? <div className="cw-error-state compact" role="alert" style={{ marginBottom: 12 }}><div className="cw-error-state-text"><p className="cw-error-state-title">Not saved</p><p className="cw-error-state-reason">{message}</p></div></div> : null);

  const meta = editing ? REPORT_TYPE_INDEX[editing] : null;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <h3 style={h3}>Report configuration</h3>
            <p className="cw-meta" style={{ margin: '3px 0 0' }}>
              How each report type is produced for {clientName || 'this client'}. Anything not configured
              uses the CloudSafe default layout and your own branding.
            </p>
          </div>
          <Chip tone={configuredCount ? 'info' : 'neutral'} label={`${configuredCount} configured`} />
        </div>

        {loading && !configs.length && !loadError ? (
          <div role="status" className="cw-meta" style={{ padding: '22px 0' }}>Loading…</div>
        ) : loadError ? (
          <div style={{ marginTop: 14 }}><ErrorState compact thing="reporting settings" error={loadError} onRetry={load} retrying={loading} /></div>
        ) : (
          <div style={{ marginTop: 18 }}>
            {REPORT_TYPES.map((g) => (
              <div key={g.group} style={{ marginBottom: 20 }}>
                <h4 className="cw-label" style={{ margin: '0 0 10px', paddingBottom: 6, borderBottom: '1px solid var(--cw-border-light)' }}>
                  {g.group}
                </h4>
                <div style={{ display: 'grid', gap: 8 }}>
                  {g.items.map((t) => {
                    const cfg = byType[t.key];
                    return (
                      <div key={t.key}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                                 border: '1px solid var(--cw-border)', borderRadius: 8, padding: '11px 14px',
                                 background: cfg ? 'var(--cw-surface)' : 'var(--cw-surface-alt)' }}>
                        <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600 }}>{t.label}</span>
                            {t.statutory && <Chip tone="purple" label="Statutory" />}
                            {cfg ? <Chip tone="info" label="Configured" /> : <Chip tone="neutral" label="Default" />}
                          </div>
                          <div className="cw-meta" style={{ marginTop: 3 }}>
                            {cfg
                              ? [
                                  cfg.template_name || 'Standard layout',
                                  labelOf(BRANDING_OPTIONS, cfg.branding),
                                  labelOf(DELIVERY_OPTIONS, cfg.delivery),
                                  labelOf(PHOTO_OPTIONS, cfg.photo_handling),
                                  cfg.structured_export !== 'none' ? labelOf(STRUCTURED_OPTIONS, cfg.structured_export) : null,
                                ].filter(Boolean).join(' · ')
                              : (t.hint || 'Using the CloudSafe default')}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          {cfg && (
                            <button type="button" className="cw-btn cw-btn-secondary" onClick={() => reset(t.key)} aria-label={`Reset ${t.label} to default`}>Reset</button>
                          )}
                          <button type="button" className="cw-btn cw-btn-primary" onClick={() => openEdit(t.key)} aria-label={`${cfg ? 'Edit' : 'Configure'} ${t.label}`}>
                            {cfg ? 'Edit' : 'Configure'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ------------------------------------------------ recurring reports */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <div>
            <h3 style={h3}>Recurring reports</h3>
            <p className="cw-meta" style={{ margin: '3px 0 0' }}>
              Compliance summaries this client receives on a schedule, independent of individual jobs.
            </p>
          </div>
          <button type="button" className="cw-btn cw-btn-primary" onClick={() => { setSubForm(SUB_INIT); setSubErrors({}); setModalError(null); setSubOpen(true); }} disabled={!!loadError}>
            <i className="fa-solid fa-plus" aria-hidden="true" /> Add recurring report
          </button>
        </div>

        {loadError ? null : subs.length === 0 ? (
          <EmptyState compact icon="fa-solid fa-calendar-days" title="No recurring reports" body="Add a scheduled summary if this client expects one." />
        ) : (
          <ul style={{ display: 'grid', gap: 10, listStyle: 'none', margin: 0, padding: 0 }}>
            {subs.map((s) => (
              <li key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, border: '1px solid var(--cw-border)', borderRadius: 8, padding: '11px 14px', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600 }}>{s.name}</span>
                    <Chip tone="neutral" label={labelOf(SUB_FREQUENCIES, s.frequency)} />
                    {Number(s.enabled) ? <Chip tone="success" label="On" dot /> : <Chip tone="muted" label="Paused" dot />}
                  </div>
                  <div className="cw-meta" style={{ marginTop: 3, overflowWrap: 'anywhere' }}>
                    {[s.template_name, s.recipients, s.output_format?.toUpperCase(),
                      s.last_sent_at ? `Last sent ${formatDate(s.last_sent_at)}` : 'Not sent yet']
                      .filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <button type="button" className="cw-btn cw-btn-secondary" onClick={() => toggleSub(s)}>{Number(s.enabled) ? 'Pause' : 'Resume'}</button>
                  <button type="button" className="cw-icon-btn" aria-label={`Remove ${s.name}`} title="Remove" onClick={() => removeSub(s)} style={{ color: 'var(--cw-danger)' }}>
                    <i className="fa-solid fa-trash" aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ------------------------------------------------------ config dialog */}
      {editing && (
        <Modal title={meta?.label || 'Report configuration'} dirty={JSON.stringify(form) !== JSON.stringify(formInitial)} onClose={() => !saving && setEditing(null)} maxWidth={720}
          footer={(
            <>
              <CancelButton disabled={saving} />
              <button type="button" className="cw-btn cw-btn-primary" onClick={save} disabled={saving} aria-busy={saving || undefined}>
                {saving ? 'Saving…' : 'Save configuration'}
              </button>
            </>
          )}>
          <FormError message={modalError} />
          <div style={{ display: 'grid', gap: 16 }}>
            {meta?.statutory && (
              <p role="note" style={{ margin: 0, fontSize: 'var(--cs-text-sm)', color: 'var(--cs-purple-dark, #5b21b6)', background: 'var(--cs-purple-light, #f5f3ff)', border: '1px solid var(--cs-purple-light, #ddd6fe)', borderRadius: 8, padding: '9px 11px', lineHeight: 1.45 }}>
                <i className="fa-solid fa-scale-balanced" style={{ marginRight: 6 }} aria-hidden="true" />
                This report satisfies a legal duty. Branding and delivery are yours to set, but content
                required by the governing standard is always included regardless of the options below.
              </p>
            )}

            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend className="cw-label" style={{ marginBottom: 10 }}>Template & branding</legend>
              <div style={grid2}>
                <Field label="Document template" hint={docTemplates.length === 0 ? "Upload templates on the Reports screen to use a client's own layout." : undefined}>
                  <select value={form.doc_template_id ?? ''} onChange={(e) => set('doc_template_id', e.target.value)}>
                    <option value="">CloudSafe standard layout</option>
                    {docTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.format ? ` (${t.format})` : ''}</option>)}
                  </select>
                </Field>
                <Field label="Branding">
                  <select value={form.branding} onChange={(e) => set('branding', e.target.value)}>
                    {BRANDING_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Output format">
                  <select value={form.output_format} onChange={(e) => set('output_format', e.target.value)}>
                    {OUTPUT_FORMATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Cover reference">
                  <input value={form.cover_reference ?? ''} placeholder="Client's internal ref"
                    onChange={(e) => set('cover_reference', e.target.value)} />
                </Field>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Named on the cover" hint="Some clients require their compliance manager named on the cover page.">
                    <select value={form.cover_contact_id ?? ''} onChange={(e) => set('cover_contact_id', e.target.value)}>
                      <option value="">Nobody</option>
                      {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.contact_type ? ` — ${c.contact_type}` : ''}</option>)}
                    </select>
                  </Field>
                </div>
              </div>
            </fieldset>

            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend className="cw-label" style={{ marginBottom: 10 }}>Content</legend>
              <div style={grid2}>
                <Field label="Photos">
                  <select value={form.photo_handling} onChange={(e) => set('photo_handling', e.target.value)}>
                    {PHOTO_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </Field>
                <div style={{ display: 'grid', gap: 8, alignContent: 'center' }}>
                  {[['include_appendices', 'Include appendices'],
                    ['include_previous_findings', 'Include previous findings'],
                    ['include_asset_register', 'Include asset register']].map(([k, l]) => (
                    <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 'var(--cs-text-sm)', minHeight: 36 }}>
                      <input type="checkbox" checked={!!Number(form[k])} onChange={(e) => set(k, e.target.checked ? 1 : 0)} />
                      {l}
                    </label>
                  ))}
                </div>
              </div>
            </fieldset>

            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend className="cw-label" style={{ marginBottom: 10 }}>Delivery</legend>
              <div style={grid2}>
                <Field label="How it's delivered">
                  <select value={form.delivery} onChange={(e) => set('delivery', e.target.value)}>
                    {DELIVERY_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Subject line">
                  <input value={form.subject_template ?? ''} placeholder="e.g. {{client}} — {{report}} — {{property}}"
                    onChange={(e) => set('subject_template', e.target.value)} />
                </Field>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Recipients" error={errors.recipients} hint="Separate addresses with commas">
                    <input type="text" inputMode="email" value={form.recipients ?? ''}
                      onChange={(e) => set('recipients', e.target.value)} />
                    {contacts.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                        {contacts.filter((c) => c.email).map((c) => (
                          <button key={c.id} type="button"
                            onClick={() => {
                              const cur = String(form.recipients || '').split(',').map((s) => s.trim()).filter(Boolean);
                              if (!cur.includes(c.email)) set('recipients', [...cur, c.email].join(', '));
                            }}
                            aria-label={`Add ${c.name} (${c.email}) to recipients`}
                            style={{ background: 'var(--cw-blue-tint)', color: 'var(--cs-blue-dark, #1565C0)', border: '1px solid var(--cs-blue-light, #dbeafe)', borderRadius: 6, padding: '4px 8px', fontSize: 'var(--cs-text-xs)', cursor: 'pointer', minHeight: 32 }}>
                            + {c.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </Field>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="CC" error={errors.cc_recipients} hint="Separate addresses with commas">
                    <input type="text" inputMode="email" value={form.cc_recipients ?? ''}
                      onChange={(e) => set('cc_recipients', e.target.value)} />
                  </Field>
                </div>
              </div>
            </fieldset>

            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend className="cw-label" style={{ marginBottom: 10 }}>Data delivery</legend>
              <div style={grid2}>
                <Field label="Structured export" hint="For clients whose CAFM system ingests compliance data rather than reading PDFs.">
                  <select value={form.structured_export} onChange={(e) => set('structured_export', e.target.value)}>
                    {STRUCTURED_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </Field>
                {form.structured_export === 'api' && (
                  <Field label="Endpoint" required error={errors.structured_endpoint}>
                    <input type="url" inputMode="url" value={form.structured_endpoint ?? ''} placeholder="https://"
                      onChange={(e) => set('structured_endpoint', e.target.value)} />
                  </Field>
                )}
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Notes">
                    <textarea style={{ minHeight: 60, resize: 'vertical' }} value={form.notes ?? ''}
                      placeholder="Anything about this client's requirements the fields above don't cover"
                      onChange={(e) => set('notes', e.target.value)} />
                  </Field>
                </div>
              </div>
            </fieldset>
          </div>
        </Modal>
      )}

      {subOpen && (
        <Modal title="Add recurring report" dirty={JSON.stringify(subForm) !== JSON.stringify(SUB_INIT)} onClose={() => !saving && setSubOpen(false)} maxWidth={560}
          footer={(
            <>
              <CancelButton disabled={saving} />
              <button type="button" className="cw-btn cw-btn-primary" onClick={addSub} disabled={saving} aria-busy={saving || undefined}>
                <i className="fa-solid fa-plus" aria-hidden="true" /> {saving ? 'Adding…' : 'Add report'}
              </button>
            </>
          )}>
          <FormError message={modalError} />
          <div style={{ display: 'grid', gap: 12 }}>
            <Field label="Name" required error={subErrors.name}>
              <input value={subForm.name} autoFocus placeholder="e.g. Monthly compliance summary"
                onChange={(e) => setSub('name', e.target.value)} />
            </Field>
            <div style={grid2}>
              <Field label="Frequency">
                <select value={subForm.frequency}
                  onChange={(e) => setSubForm((f) => ({ ...f, frequency: e.target.value }))}>
                  {SUB_FREQUENCIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </Field>
              <Field label="Format">
                <select value={subForm.output_format}
                  onChange={(e) => setSubForm((f) => ({ ...f, output_format: e.target.value }))}>
                  {OUTPUT_FORMATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Report definition">
              <select value={subForm.report_template_id}
                onChange={(e) => setSubForm((f) => ({ ...f, report_template_id: e.target.value }))}>
                <option value="">Choose later</option>
                {dataTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
            <Field label="Recipients" error={subErrors.recipients} hint="Separate addresses with commas">
              <input type="text" inputMode="email" value={subForm.recipients}
                onChange={(e) => setSub('recipients', e.target.value)} />
            </Field>
            <Field label="What it covers">
              <textarea style={{ minHeight: 60, resize: 'vertical' }} value={subForm.contents}
                placeholder="e.g. All properties, compliance status, open remedials"
                onChange={(e) => setSubForm((f) => ({ ...f, contents: e.target.value }))} />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ClientReporting;
